import { css, type CSSResult } from 'lit';
import {
  DEFAULT_THEME,
  type ButtonShape,
  type ThemeConfig,
} from '../config/types';

/** Border-radius each `buttonShape` maps to for the `--zui-btn-radius` token. */
const BUTTON_SHAPE_RADIUS: Record<ButtonShape, string> = {
  square: '0',
  rounded: '8px',
  pill: '999px',
};

type Rgb = { r: number; g: number; b: number };

/** Parse `#rgb` / `#rrggbb` into channels; returns null for anything else. */
function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex({ r, g, b }: Rgb): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Darken a color toward black by `amount` (0–1) — the accent's pressed/deep shade. */
function darken(hex: string, amount: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  return toHex({ r: c.r * (1 - amount), g: c.g * (1 - amount), b: c.b * (1 - amount) });
}

/** Light wash: mix `hex` into white, keeping `amount` (0–1) of the accent. */
function wash(hex: string, amount: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  const mix = (v: number) => 255 * (1 - amount) + v * amount;
  return toHex({ r: mix(c.r), g: mix(c.g), b: mix(c.b) });
}

/**
 * First candidate that is a solid hex colour, normalised to `#rrggbb`; white
 * when none is. Only 3/6-digit hex qualifies: those can't carry alpha, while
 * `transparent`, `rgba(...)`, 8-digit hex or a typo could all leave a surface
 * see-through (an invalid value set on a custom property also defeats the
 * `var(--x, #fff)` fallback, since the property IS set).
 */
function solidColor(...candidates: string[]): string {
  for (const c of candidates) {
    const rgb = parseHex(c);
    if (rgb) return toHex(rgb);
  }
  return '#ffffff';
}

/**
 * Build the inline `style` value that maps theme tokens to CSS custom
 * properties on a component's shadow host. Components reference these as
 * `var(--zui-accent)` etc. in their styles, so a config change re-themes the
 * whole component without touching component CSS.
 *
 * `accentDeep`/`accentSoft` are DERIVED from the chosen `accent` when the
 * config doesn't set them, so a custom accent stays on-brand instead of
 * falling back to the old fixed teal. `buttonShape` (a component option, not a
 * theme token) maps to `--zui-btn-radius`, defaulting to the theme radius token.
 */
export function themeToCssVars(
  theme: Partial<ThemeConfig> = {},
  buttonShape?: ButtonShape,
): string {
  const t = { ...DEFAULT_THEME, ...theme };
  const accentDeep = t.accentDeep ?? darken(t.accent, 0.24);
  const accentSoft = t.accentSoft ?? wash(t.accent, 0.17);
  const btnRadius =
    buttonShape && BUTTON_SHAPE_RADIUS[buttonShape]
      ? BUTTON_SHAPE_RADIUS[buttonShape]
      : 'var(--zui-radius)';
  const vars = [
    `--zui-accent:${t.accent}`,
    `--zui-accent-deep:${accentDeep}`,
    `--zui-accent-soft:${accentSoft}`,
    `--zui-bg:${t.background}`,
    `--zui-panel:${t.panel}`,
    `--zui-line:${t.lineColor}`,
    `--zui-headline:${t.headline}`,
    `--zui-details:${t.details}`,
    `--zui-panel-ink:${t.panelInk}`,
    `--zui-panel-ink-soft:${t.panelInkSoft}`,
    `--zui-font:${t.font}`,
    `--zui-weight:${t.weight}`,
    `--zui-radius:${t.radius}px`,
    `--zui-btn-radius:${btnRadius}`,
    `--zui-btn-text:${t.buttonTextColor}`,
    // Sticky chrome (schedule filter + date strip) needs a surface list rows
    // can never show through. Host pages override '--zui-bg' — some set it to
    // 'transparent !important' so the widget blends into their page — so the
    // sticky block gets its own always-opaque token instead of sharing it.
    `--zui-sticky-bg:${solidColor(t.background, t.panel)}`,
  ];
  // X2: "square" must flatten item cards + filters too, not just buttons.
  // Emit this token ONLY for square so `var(--zui-square-radius, <normal>)`
  // on those selectors resolves to 0 when square and keeps its own radius for
  // rounded/pill/unset (which must stay unchanged).
  if (buttonShape === 'square') {
    vars.push('--zui-square-radius:0');
  }
  return vars.join(';');
}

/**
 * Set the accent tokens (`--zui-accent` + derived deep/soft) as INLINE host
 * styles on `el` so `var(--zui-accent*)` inside a nested component reflects the
 * embed's configured accent.
 *
 * Top-level embeds get these via {@link themeToCssVars} in the base element, and
 * custom properties inherit through shadow boundaries — but a nested component
 * (checkout, sign-in) carries `baseStyles`' `:host { --zui-accent: … }` default,
 * which shadows the inherited value and pins the accent to the fixed teal. An
 * inline host style outranks that `:host` rule, restoring the real accent for the
 * component AND anything it renders (incl. the light-DOM payment element). Call
 * from `willUpdate` when the `accent` property changes.
 */
export function applyAccentVars(el: HTMLElement, accent: string): void {
  if (!accent) return;
  el.style.setProperty('--zui-accent', accent);
  el.style.setProperty('--zui-accent-deep', darken(accent, 0.24));
  el.style.setProperty('--zui-accent-soft', wash(accent, 0.17));
}

/**
 * SC6: promote an already-rendered `position:fixed` overlay (month picker,
 * filter sheet, filter-pill modal) to the browser top layer via the Popover
 * API so it is laid out against the VIEWPORT, not the embed.
 *
 * A plain `position:fixed` overlay is anchored to the nearest ancestor that
 * establishes a containing block — any host-page wrapper with `transform`,
 * `filter`, `perspective`, `contain`, or `will-change`. On a long/infinite
 * schedule that ancestor is the full-height embed, so the menu renders
 * centered on the embed's scroll height and lands far below the fold. Top-layer
 * elements ignore ancestor containing blocks (and overflow clipping), so the
 * overlay re-centers in the visible viewport regardless of scroll position.
 *
 * The overlay markup must carry `popover="manual"`; call this once it is in the
 * DOM (e.g. from `updated()`). No-op — leaving the plain fixed overlay as a
 * graceful fallback — when the Popover API is unavailable.
 */
export function showAsTopLayer(el: Element | null | undefined): void {
  const node = el as (HTMLElement & { showPopover?: () => void }) | null;
  if (!node || typeof node.showPopover !== 'function') return;
  try {
    if (!node.matches(':popover-open')) node.showPopover();
  } catch {
    /* Older engines: the plain position:fixed overlay stays as the fallback. */
  }
}

/** Base styles shared by every embed: token-driven defaults + reset. */
export const baseStyles: CSSResult = css`
  :host {
    display: block;
    box-sizing: border-box;
    font-family: var(--zui-font, system-ui, sans-serif);
    font-weight: var(--zui-weight, 500);
    color: var(--zui-headline, #1f2a22);
    background: var(--zui-bg, #fff);
    --zui-accent: #4f9db5;
  }
  :host *,
  :host *::before,
  :host *::after {
    box-sizing: border-box;
  }
  .zui-cta {
    appearance: none;
    border: 0;
    cursor: pointer;
    font: inherit;
    color: var(--zui-btn-text, #fff);
    background: var(--zui-accent);
    border-radius: var(--zui-btn-radius, var(--zui-radius, 8px));
    padding: 0.625rem 1rem;
  }
  .zui-cta:hover {
    background: var(--zui-accent-deep, var(--zui-accent));
  }
  /* Secondary (outline) button — the counterpart to .zui-cta for an action that
     sits beside the primary flow CTA but must read as subordinate to it (the
     "Sign in" affordance on a section headline row). Token-driven like .zui-cta
     so accent/radius/button-text theming applies with no per-embed overrides;
     'white-space: nowrap' keeps a two-word label off two lines in a cramped
     row, and the 44px floor keeps it a real tap target on phones. */
  .zui-btn-secondary {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    color: var(--zui-accent);
    background: transparent;
    border: 1px solid var(--zui-accent);
    border-radius: var(--zui-btn-radius, var(--zui-radius, 8px));
    padding: 0.5rem 0.9rem;
    min-height: 44px;
  }
  /* Normalize the leading icon here rather than trusting each embed's local
     ICON_SIGNIN dimensions (they range 13px–16px), so every sign-in button
     renders identically no matter which component it came from. */
  .zui-btn-secondary svg {
    width: 13px;
    height: 13px;
    flex: 0 0 auto;
  }
  .zui-btn-secondary:hover {
    color: var(--zui-btn-text, #fff);
    background: var(--zui-accent);
  }
  .zui-btn-secondary:focus-visible {
    outline: 2px solid var(--zui-accent-deep, var(--zui-accent));
    outline-offset: 2px;
  }
  .zui-state {
    padding: 1.5rem;
    color: var(--zui-details, #516056);
    font-size: 0.9rem;
  }
  .zui-powered {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    padding: 2.25rem 0 0.5rem;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9ca3af;
    text-decoration: none;
    cursor: pointer;
  }
  .zui-powered:hover {
    color: #6b7280;
  }
  .zui-powered svg {
    height: 18px;
  }

  /* Loading skeleton (shimmer), shaped like a schedule card. */
  .zui-skeleton {
    padding: 1rem 0 1.5rem;
  }
  .zui-skel-strip {
    display: flex;
    gap: 0.5rem;
    padding: 0 1.5rem 1rem;
    overflow: hidden;
  }
  .zui-skel-pill {
    width: 44px;
    height: 64px;
    border-radius: var(--zui-radius, 8px);
    flex-shrink: 0;
  }
  .zui-skel-rows {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 1.5rem;
  }
  .zui-skel-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .zui-skel-thumb {
    width: 56px;
    height: 56px;
    border-radius: var(--zui-radius, 8px);
    flex-shrink: 0;
  }
  .zui-skel-lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .zui-skel-line {
    height: 10px;
    border-radius: 4px;
  }
  .zui-skel-line-lg {
    width: 60%;
  }
  .zui-skel-line-sm {
    width: 35%;
  }
  .zui-skel-pill,
  .zui-skel-thumb,
  .zui-skel-line {
    background: linear-gradient(90deg, #eceef1 25%, #f6f7f9 37%, #eceef1 63%);
    background-size: 400% 100%;
    animation: zui-shimmer 1.4s ease infinite;
  }
  @keyframes zui-shimmer {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .zui-skel-pill,
    .zui-skel-thumb,
    .zui-skel-line {
      animation: none;
    }
  }
`;
